import { useRef, useState, useEffect } from "react";
import { Settings } from "lucide-react";
import { useAuth } from "../../context/authContext";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import PortalShellPageHeader from "../../components/Layout/PortalShellPageHeader/PortalShellPageHeader";
import portalStyles from "../../layout/portalPageShell.module.css";
import styles from "./SettingsPage.module.css";
import LoadingOverlay from "../../components/UI/LoadingOverlay/LoadingOverlay";
import DefaultUserImage from "../../assets/images/default-user.webp";

export default function SettingsPage() {
  const { user, logout, updateUser } = useAuth();
  const { t, i18n } = useTranslation(["common", "navigation", "language"]);
  const navigate = useNavigate();
  const [isLanguageDropdownOpen, setIsLanguageDropdownOpen] = useState(false);
  const [isLanguageSwitching, setIsLanguageSwitching] = useState(false);
  const dropdownRef = useRef(null);

  const handleLogout = async () => {
    try {
      await logout();
    } catch {
      alert("Logout failed. Please try again.");
    }
  };

  const handleEditProfile = () => {
    navigate("/profile");
  };

  const changeLanguage = async (language) => {
    setIsLanguageDropdownOpen(false);
    setIsLanguageSwitching(true);

    try {
      await i18n.changeLanguage(language);
      localStorage.setItem("i18nextLng", language);
      setTimeout(() => {
        setIsLanguageSwitching(false);
      }, 800);
    } catch {
      setIsLanguageSwitching(false);
    }
  };

  const toggleLanguageDropdown = () => {
    setIsLanguageDropdownOpen((prev) => !prev);
  };

  useEffect(() => {
    const savedCurrency = localStorage.getItem("userCurrency");
    if (savedCurrency && user && !user.currency) {
      updateUser({ ...user, currency: savedCurrency });
    }
  }, [user, updateUser]);

  useEffect(() => {
    const savedLanguage = localStorage.getItem("i18nextLng");
    if (savedLanguage && i18n.language !== savedLanguage) {
      i18n.changeLanguage(savedLanguage);
    }
  }, [i18n]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsLanguageDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const currentLanguage =
    i18n.language || localStorage.getItem("i18nextLng") || "en";

  const languages = [
    { code: "ro", name: "Română", flag: "🇷🇴" },
    { code: "en", name: "English", flag: "🇺🇸" },
  ];

  if (!user) {
    return (
      <div className={portalStyles.portalPage}>
        <div className={portalStyles.container}>
          <PortalShellPageHeader
            icon={
              <Settings size={28} strokeWidth={1.75} absoluteStrokeWidth />
            }
            title={t("common:settingsPage.title")}
            subtitle={t("common:settingsPage.subtitle")}
          />
          <div className={styles.settingsInner}>
            <div className={styles.loadingMessage}>{t("common:loading")}</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <LoadingOverlay
        isVisible={isLanguageSwitching}
        message={isLanguageSwitching ? t("language:switching") : ""}
        showLogo={true}
        minDisplayTime={500}
      />
      <div className={portalStyles.portalPage}>
        <div className={portalStyles.container}>
          <PortalShellPageHeader
            icon={
              <Settings size={28} strokeWidth={1.75} absoluteStrokeWidth />
            }
            title={t("common:settingsPage.title")}
            subtitle={t("common:settingsPage.subtitle")}
          />
          <div className={styles.settingsInner}>
            <div className={styles.settingsCard}>
              <div className={styles.userSection}>
                <div className={styles.userInfo}>
                  <div className={styles.profileImageContainer}>
                    <img
                      src={user.profileImage || DefaultUserImage}
                      referrerPolicy="no-referrer"
                      alt={`${user.name || "User"}'s profile`}
                      className={styles.profileImage}
                      onError={(e) => {
                        e.currentTarget.onerror = null;
                        e.currentTarget.src = DefaultUserImage;
                      }}
                    />
                  </div>
                  <div className={styles.userDetails}>
                    <h2 className={styles.userName}>
                      {user.name || "Unknown User"}
                    </h2>
                    <p className={styles.userEmail}>
                      {user.email || "No email available"}
                    </p>
                    <p className={styles.authProvider}>
                      {t("common:settings.signedInWithGoogle")}
                    </p>
                  </div>
                </div>
              </div>

              <hr className={styles.divider} />

              <div className={styles.profileSection}>
                <div className={styles.profileInfo}>
                  <div className={styles.sectionHeader}>
                    <svg
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      className={styles.sectionIcon}
                    >
                      <path
                        d="M12 12C14.21 12 16 10.21 16 8C16 5.79 14.21 4 12 4C9.79 4 8 5.79 8 8C8 10.21 9.79 12 12 12ZM12 14C9.33 14 4 15.34 4 18V20H20V18C20 15.34 14.67 14 12 14Z"
                        fill="currentColor"
                      />
                    </svg>
                    <h3 className={styles.profileTitle}>
                      {t("navigation:profile")}
                    </h3>
                  </div>
                  <p className={styles.profileDescription}>
                    {t("common:settings.updateProfileInformation")}
                  </p>
                </div>
                <button
                  type="button"
                  className={styles.editProfileBtn}
                  onClick={handleEditProfile}
                >
                  {t("common:settings.editProfile")}
                </button>
              </div>

              <hr className={styles.divider} />

              <div className={styles.languageSection}>
                <div className={styles.languageInfo}>
                  <div className={styles.sectionHeader}>
                    <svg
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      className={styles.sectionIcon}
                    >
                      <path
                        d="M12.87 15.07L10.33 12.56L10.36 12.53C12.1 10.59 13.34 8.36 14.07 6H17V4H10V2H8V4H1V6H12.17C11.5 7.92 10.44 9.75 9 11.35C8.07 10.32 7.3 9.19 6.69 8H4.69C5.42 9.63 6.42 11.17 7.67 12.56L2.58 17.58L4 19L9 14L12.11 17.11L12.87 15.07ZM18.5 10H16.5L12 22H14L15.12 19H19.87L21 22H23L18.5 10ZM15.88 17L17.5 12.67L19.12 17H15.88Z"
                        fill="currentColor"
                      />
                    </svg>
                    <h3 className={styles.languageTitle}>
                      {t("language:title")}
                    </h3>
                  </div>
                  <p className={styles.languageDescription}>
                    {t("language:description")}
                  </p>
                </div>
                <div className={styles.languageSwitcher} ref={dropdownRef}>
                  <button
                    type="button"
                    onClick={toggleLanguageDropdown}
                    className={styles.languageButton}
                    aria-label="Select Language"
                  >
                    <div className={styles.languageButtonContent}>
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        className={styles.languageIcon}
                      >
                        <path
                          d="M12.87 15.07L10.33 12.56L10.36 12.53C12.1 10.59 13.34 8.36 14.07 6H17V4H10V2H8V4H1V6H12.17C11.5 7.92 10.44 9.75 9 11.35C8.07 10.32 7.3 9.19 6.69 8H4.69C5.42 9.63 6.42 11.17 7.67 12.56L2.58 17.58L4 19L9 14L12.11 17.11L12.87 15.07ZM18.5 10H16.5L12 22H14L15.12 19H19.87L21 22H23L18.5 10ZM15.88 17L17.5 12.67L19.12 17H15.88Z"
                          fill="currentColor"
                        />
                      </svg>
                      <span className={styles.currentLanguage}>
                        {languages.find((lang) => lang.code === currentLanguage)
                          ?.name || "English"}
                      </span>
                      <svg
                        width="12"
                        height="12"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        className={`${styles.dropdownArrow} ${
                          isLanguageDropdownOpen ? styles.dropdownArrowOpen : ""
                        }`}
                      >
                        <path d="M7 10L12 15L17 10H7Z" fill="currentColor" />
                      </svg>
                    </div>
                  </button>

                  {isLanguageDropdownOpen && (
                    <div className={styles.languageDropdown}>
                      {languages.map((language) => (
                        <button
                          type="button"
                          key={language.code}
                          onClick={() => changeLanguage(language.code)}
                          className={`${styles.languageOption} ${
                            currentLanguage === language.code
                              ? styles.languageOptionActive
                              : ""
                          }`}
                        >
                          <span className={styles.languageFlag}>
                            {language.flag}
                          </span>
                          <span className={styles.languageName}>
                            {language.name}
                          </span>
                          {currentLanguage === language.code && (
                            <svg
                              width="16"
                              height="16"
                              viewBox="0 0 24 24"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                              className={styles.checkIcon}
                            >
                              <path
                                d="M9 16.17L4.83 12L3.41 13.41L9 19L21 7L19.59 5.59L9 16.17Z"
                                fill="currentColor"
                              />
                            </svg>
                          )}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <hr className={styles.divider} />

              <div className={styles.logoutSection}>
                <div className={styles.logoutInfo}>
                  <div className={styles.sectionHeader}>
                    <svg
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      className={styles.sectionIcon}
                    >
                      <path
                        d="M17 7L15.59 8.41L18.17 11H8V13H18.17L15.59 15.59L17 17L22 12L17 7ZM4 5H12V3H4C2.9 3 2 3.9 2 5V19C2 20.1 2.9 21 4 21H12V19H4V5Z"
                        fill="currentColor"
                      />
                    </svg>
                    <h3 className={styles.logoutTitle}>{t("common:logout")}</h3>
                  </div>
                  <p className={styles.logoutDescription}>
                    {t("common:settings.logoutFromAccount")}
                  </p>
                </div>
                <button
                  type="button"
                  className={styles.logoutBtn}
                  onClick={handleLogout}
                >
                  {t("common:logout")}
                </button>
              </div>
            </div>

            <div className={styles.additionalInfo}>
              <div className={styles.infoCard}>
                <h3 className={styles.infoTitle}>
                  {t("common:settings.accountInformation")}
                </h3>
                <p className={styles.infoText}>
                  {t("common:settings.accountInformationDescription")}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
